import { toZonedDateTime, toInstant, getTemporalIds, instant } from '#library/temporal.library.js';
import { isDefined, isFunction, isObject, isString, isUndefined, isNumber, isZonedDateTime, isPlainObject, isText, isSafeKey, isNullish } from '#library/assertion.library.js';
import { secure } from '#library/proxy.library.js';
import { sortKey, byKey } from '#library/array.library.js';
import { asArray, asError } from '#library/coercion.library.js';
import { deepFreeze } from '#library/utility.library.js';
import { cast } from '#library/type.library.js';
import { TEMPO_VERSION } from '../../tempo.version.js';
import { getHost } from '../plugin.util.js';
import { sym, TermError, isTempo } from '../../support/support.symbol.js';
import { getRuntime } from '../../support/support.runtime.js';
import { SCHEMA, getLargestUnit } from '../../support/support.util.js';
/**
 * ## defineTerm
 * Helper to register a Term plugin.
 */
export const defineTerm = (term) => {
    const aliasesSet = new Set();
    for (const a of asArray(term.aliases))
        if (a !== term.key)
            aliasesSet.add(a);
    if (term.scope && term.scope !== term.key)
        aliasesSet.add(term.scope);
    const termObj = {
        ...term,
        ...(aliasesSet.size > 0 ? { aliases: Array.from(aliasesSet) } : {}),
        [sym.$PluginType]: 'term',
        version: term.version ?? TEMPO_VERSION
    };
    const factory = function (options) {
        return deepFreeze({
            ...termObj,
            options: isObject(options) ? { ...options } : options,
            [sym.$PluginType]: 'term'
        });
    };
    const { name, ...rest } = termObj;
    const result = Object.assign(factory, rest, { [sym.$PluginType]: 'term' });
    if (name) {
        Object.defineProperty(result, 'name', { value: name, configurable: true });
    }
    registerTerm(result);
    return deepFreeze(result);
};
/**
 * Finds a term plugin by its key, scope, alias, or range key.
 *
 * @param ident - The term or range identifier, optionally prefixed with `#`
 * @param state - Optional plugin state to search
 * @returns The matching term plugin, or `undefined` when no match is found
 */
export function findTermPlugin(ident, state) {
    if (!isString(ident))
        return undefined;
    const id = (ident.startsWith('#') ? ident.slice(1) : ident).toLowerCase();
    const [termPart] = id.split('.');
    const st = state?.pluginsDb ? state : getRuntime().state;
    if (!st?.pluginsDb?.terms)
        return undefined;
    return st.pluginsDb.terms.find((t) => {
        if (t.key?.toLowerCase() === termPart || t.scope?.toLowerCase() === termPart)
            return true;
        if (asArray(t.aliases).some((a) => a.toLowerCase() === termPart))
            return true;
        if (t.groups) {
            const list = Array.isArray(t.groups) ? t.groups : Object.values(t.groups).flat(Infinity);
            return list.some((r) => r.key?.toLowerCase() === id || r.key?.toLowerCase() === termPart);
        }
        return false;
    });
}
/**
 * Groups and secures term ranges for efficient lookup.
 *
 * @param ranges - The ranges to group
 * @param keys - Range properties used as grouping keys
 * @returns A secured collection of grouped ranges
 */
export function defineRange(ranges, ...keys) {
    return secure(byKey(ranges, ...keys));
}
/**
 * Resolves the range containing a Tempo instance.
 *
 * @param tempo - The Tempo instance to locate
 * @param list - The ranges to search
 * @param keyOnly - Controls the result: `true` returns the range key, a positive integer returns the corresponding cycle range, and `false` returns the resolved range
 * @param anchor - Optional temporal anchor used to resolve range boundaries
 * @returns The matching range key, resolved range, or `undefined` when no range is available
 * @internal
 */
export function getTermRange(tempo, list, keyOnly = true, anchor) {
    const chronological = sortKey([...list], 'year', 'month', 'day', 'hour', 'minute', 'second', 'millisecond', 'microsecond', 'nanosecond');
    if (chronological.length === 0)
        return undefined;
    let zdt = isTempo(anchor)
        ? anchor.toDateTime()
        : (isZonedDateTime(anchor) ? anchor : (isTempo(tempo) ? tempo.toDateTime() : undefined));
    if (!isZonedDateTime(zdt))
        zdt = instant().toZonedDateTimeISO(tempo.config.timeZone ?? 'UTC');
    // determine the largest unit defined in the range list, and use the unit above it as rollover
    const unit = getLargestUnit(list);
    const unitIndex = SCHEMA.findIndex(([u]) => u === unit);
    const rolloverIndex = Math.max(0, unitIndex - 1);
    const rolloverUnit = SCHEMA[rolloverIndex][0];
    const resolve = (range, anchor) => {
        const obj = {};
        for (let i = 0; i < SCHEMA.length; i++) {
            const [u] = SCHEMA[i];
            const val = range[u];
            if (isNumber(val)) {
                obj[u] = val;
            }
            else if (i > rolloverIndex) {
                obj[u] = (i <= 2) ? 1 : 0;
            }
            else {
                const fallback = anchor[u];
                obj[u] = isNumber(fallback) ? fallback : (i <= 2 ? 1 : 0);
            }
        }
        // @ts-ignore
        const [timeZone, calendar] = getTemporalIds(anchor.timeZoneId, anchor.calendarId);
        const resZdt = toZonedDateTime({ ...obj, timeZone, calendar });
        // @ts-ignore
        return new (getHost(tempo))(resZdt, tempo.config);
    };
    const matchIndex = chronological.findLastIndex(range => {
        const date = resolve(range, zdt);
        return date.toDateTime().epochNanoseconds <= zdt.epochNanoseconds;
    });
    if (isNumber(keyOnly)) {
        const cycle = Math.floor(chronological.length / 3);
        if (cycle === 0 || !Number.isInteger(keyOnly) || keyOnly < 1 || keyOnly > cycle)
            return undefined;
        const offset = matchIndex === -1 ? cycle : Math.floor(matchIndex / cycle) * cycle;
        const match = chronological[offset + (keyOnly - 1)];
        if (!match)
            return undefined;
        const start = resolve(match, zdt);
        let end;
        const i = offset + (keyOnly - 1);
        const next = chronological[i + 1];
        if (next) {
            end = resolve(next, zdt);
        }
        else {
            const roll = { ...match };
            if (isNumber(roll.year))
                roll.year++;
            end = resolve(roll, zdt.add({ [`${rolloverUnit}s`]: 1 }));
        }
        return {
            start,
            end,
            ...match
        };
    }
    if (isText(keyOnly)) {
        const cycle = Math.floor(chronological.length / 3);
        const offset = matchIndex === -1 ? cycle : Math.floor(matchIndex / cycle) * cycle;
        const cycleSlice = cycle > 0 ? chronological.slice(offset, offset + cycle) : chronological;
        const lowKey = keyOnly.toLowerCase();
        const match = chronological.find(r => r.year === zdt.year && (r.key?.toLowerCase() === lowKey || r.label?.toLowerCase() === lowKey))
            ?? cycleSlice.find(r => r.key?.toLowerCase() === lowKey || r.label?.toLowerCase() === lowKey)
            ?? chronological.find(r => r.key?.toLowerCase() === lowKey || r.label?.toLowerCase() === lowKey);
        if (!match)
            return undefined;
        const start = resolve(match, zdt);
        const i = chronological.indexOf(match);
        const next = chronological[i + 1];
        let end;
        if (next) {
            end = resolve(next, zdt);
        }
        else {
            const roll = { ...match };
            if (isNumber(roll.year))
                roll.year++;
            end = resolve(roll, zdt.add({ [`${rolloverUnit}s`]: 1 }));
        }
        return {
            start,
            end,
            ...match
        };
    }
    const match = chronological[matchIndex === -1 ? 0 : matchIndex];
    if (keyOnly === true)
        return match.key;
    const i = chronological.indexOf(match);
    const next = chronological[i + 1];
    const start = resolve(match, zdt);
    let end;
    if (next) {
        end = resolve(next, zdt);
    }
    else {
        const roll = { ...match };
        if (isNumber(roll.year))
            roll.year++;
        end = resolve(roll, zdt.add({ [`${rolloverUnit}s`]: 1 }));
    }
    return {
        start,
        end,
        ...match
    };
}
/**
 * Resolves and filters the ranges provided by a term plugin.
 *
 * @param entry - The term plugin entry to resolve
 * @param anchor - Optional anchor used as the resolution context
 * @param group - Optional group name used to filter the ranges
 * @returns The resolved, filtered range list
 * @internal
 */
export function getRange(entry, t, anchor, group) {
    const term = (entry.plugin ?? entry);
    let res;
    try {
        if (isDefined(anchor)) {
            const host = new (getHost(t))(anchor, t.config);
            res = isFunction(term.resolve) ? term.resolve.call(host, anchor) : term.define.call(host, false, anchor);
        }
        else {
            res = isFunction(term.resolve) ? term.resolve.call(t) : term.define.call(t, false);
        }
    }
    catch (err) {
        const error = asError(err);
        if (error.message.includes('Class constructor'))
            return [];
        throw error;
    }
    let list = (res == null) ? [] : asArray(res);
    const keys = term.groupBy ?? [];
    if (keys.length > 0) {
        list = list.filter(r => keys.every((key) => {
            if (key === 'sphere') {
                const valA = String(r[key] ?? '').toLowerCase();
                const valB = String(t.sphere ?? t.config[key] ?? '').toLowerCase();
                if (valA === '' || valB === '')
                    return false;
                return valB.includes(valA);
            }
            return r[key] === (t[key] ?? t.config[key]);
        }));
    }
    if (group) {
        const meta = term.groups ?? term.ranges;
        if (isPlainObject(meta)) {
            const source = Object.values(meta).flat(Infinity);
            list = source.filter(r => r.group === group);
        }
        else {
            list = list.filter(r => r.group === group);
        }
    }
    return secure(list);
}
/**
 * Resolves a term to a boundary of its current range.
 *
 * @param tempo - The reference time used to determine the current range
 * @param terms - The available term definitions
 * @param offset - The term key, scope, or alias, optionally prefixed with `#`
 * @param mutate - The boundary to resolve: `start`, `mid`, or `end`
 * @returns The range start, midpoint, or final instant before its end, or `undefined` when unavailable
 * @internal
 */
export function resolveTermAnchor(tempo, terms, offset, mutate) {
    const ident = offset.startsWith('#') ? offset.slice(1) : offset;
    const termObj = terms.find(t => t.key === ident || t.scope === ident || asArray(t.aliases).includes(ident));
    if (!termObj)
        return undefined;
    const anchor = tempo.toDateTime();
    const list = getRange(termObj, tempo, anchor);
    const range = getTermRange(tempo, list, false, anchor);
    if (!range)
        return undefined;
    if (mutate === 'start')
        return range.start;
    if (mutate === 'mid') {
        const startNs = range.start.toDateTime().epochNanoseconds;
        const endNs = range.end.toDateTime().epochNanoseconds;
        const midNs = startNs + (endNs - startNs) / 2n;
        // @ts-ignore
        return new tempo.constructor(toInstant(midNs).toZonedDateTimeISO(range.start.tz).withCalendar(range.start.cal), tempo.config);
    }
    if (mutate === 'end')
        return range.end.subtract({ nanoseconds: 1 });
    return undefined;
}
/**
 * Resolves the start boundary of a range at a shifted position relative to the current term range.
 *
 * @param tempo - The reference date and time used to identify the current range.
 * @param source - Term plugins or pre-resolved ranges to search.
 * @param offset - The term key, scope, or alias when `source` contains plugins.
 * @param shift - The number of ranges to move forward or backward.
 * @returns The start boundary of the shifted range, or `undefined` if the source, current range, or target range cannot be resolved.
 * @internal
 */
export function resolveTermShift(tempo, source, offset, shift) {
    const anchor = tempo.toDateTime();
    let list = [];
    // If source is a list of plugins, find the right one and resolve it.
    // Otherwise, it's a pre-resolved list of ranges.
    if (source.length > 0 && 'define' in source[0]) {
        const ident = offset.startsWith('#') ? offset.slice(1) : offset;
        const termObj = source.find(t => t.key === ident || t.scope === ident || asArray(t.aliases).includes(ident));
        if (!termObj)
            return undefined;
        list = getRange(termObj, tempo, anchor);
    }
    else {
        list = source;
    }
    const range = getTermRange(tempo, list, false, anchor);
    if (!range)
        return undefined;
    // find index in list (matching key and major components for identity)
    const idx = list.findIndex(r => {
        return r.key === range.key &&
            (isUndefined(r.year) || isUndefined(range.year) || r.year === range.year) &&
            (isUndefined(r.month) || isUndefined(range.month) || r.month === range.month) &&
            (isUndefined(r.day) || isUndefined(range.day) || r.day === range.day);
    });
    if (idx === -1)
        return undefined;
    const targetIdx = idx + shift;
    const target = list[targetIdx];
    if (!target)
        return undefined;
    // resolve target range
    const res = getTermRange(tempo, [target], false);
    if (!res)
        return undefined;
    return res.start;
}
/**
 * Builds a three-period range window centered on a source date.
 *
 * @param source - The date value or `Tempo` instance used to determine the window.
 * @param template - Range templates provided as an array or grouped record.
 * @param options - Configuration for the anchor and template grouping criteria.
 * @returns Detached range copies representing the previous, current, and next periods.
 * @internal
 */
export function resolveCycleWindow(source, template, { anchor, groupBy = [], ...options } = {}) {
    // ensure we have a valid Tempo instance to work with
    const t = (isTempo(source) ? source : (isDefined(source) ? new (getHost(source))(source) : source));
    if (!isTempo(t))
        return [];
    // 1. Resolve Template (supporting optional dynamic grouping)
    let list = [];
    if (!isDefined(template)) {
        t.constructor[TermError]?.(t.config, 'template');
        return [];
    }
    if (!Array.isArray(template) && groupBy.length > 0) {
        const groupKey = groupBy
            .map(key => options[key] ?? anchor?.[key] ?? t[key] ?? t.config[key] ?? '')
            .join('.');
        list = template[groupKey] ?? [];
        if (list.length === 0 && groupBy.includes('sphere')) {
            const sphereIdx = groupBy.indexOf('sphere');
            const targetParts = groupKey.split('.');
            const targetSphere = (targetParts.length > sphereIdx ? targetParts[sphereIdx] : '').toLowerCase();
            let bestKey;
            let bestSphereLength = -1;
            for (const k of Object.keys(template)) {
                const kParts = k.split('.');
                if (kParts.length !== targetParts.length)
                    continue;
                const sphereSegment = (kParts[sphereIdx] ?? '').trim();
                if (!sphereSegment)
                    continue;
                const staticMatch = kParts.every((p, i) => i === sphereIdx || p === targetParts[i]);
                if (!staticMatch)
                    continue;
                const sphereLower = sphereSegment.toLowerCase();
                const sphereMatch = targetSphere.includes(sphereLower);
                if (!sphereMatch)
                    continue;
                if (sphereSegment.length > bestSphereLength ||
                    (sphereSegment.length === bestSphereLength && (!bestKey || k.localeCompare(bestKey) < 0))) {
                    bestKey = k;
                    bestSphereLength = sphereSegment.length;
                }
            }
            if (bestKey)
                list = template[bestKey];
        }
        if (list.length === 0) {
            const missing = groupBy.filter(k => isUndefined(options[k]) && isUndefined(anchor?.[k]) && isUndefined(t[k]) && isUndefined(t.config[k]));
            const msg = missing.length > 0 ? `Missing grouping criteria: ${missing.join(', ')}` : `No ranges found for group: ${groupKey}`;
            t.constructor[TermError]?.(t.config, msg);
            return [];
        }
    }
    else {
        list = Array.isArray(template) ? template : Object.values(template).flat();
    }
    if (list.length === 0)
        return [];
    // Ensure chronological order for reliable anchor/window calculation
    list = [...list]; // Defensive copy to avoid mutating the original template
    sortKey(list, 'year', 'month', 'day', 'hour', 'minute', 'second', 'millisecond', 'microsecond', 'nanosecond');
    // 2. Resolve Window (Sub-Yearly vs Yearly)
    const unit = getLargestUnit(list);
    if (!['year', 'month', 'day'].includes(unit)) {
        const results = [];
        for (const offset of [-1, 0, 1]) {
            const date = t.add({ days: offset });
            list.forEach(itm => {
                results.push({
                    ...itm,
                    year: date.yy,
                    month: date.mm,
                    day: date.dd
                });
            });
        }
        return results;
    }
    // Handle Yearly Cycles (Default)
    const yy = t.yy;
    const mm = t.mm;
    const dd = t.dd;
    const startItem = list[0];
    const startMm = startItem.month ?? 1;
    const startDd = startItem.day ?? 1;
    let baseYear = yy;
    if (mm < startMm || (mm === startMm && dd < startDd))
        baseYear--;
    const window = [];
    for (const offset of [-1, 0, 1]) {
        const targetYY = baseYear + offset;
        list.forEach(itm => {
            const clone = { ...itm };
            // Normalize year semantics: Treat small offsets as relative to the cycle,
            // while treating larger numbers as absolute years (e.g. for fixed historical dates).
            if (isNumber(itm.year)) {
                clone.year = (itm.year >= -10 && itm.year <= 10) ? itm.year + targetYY : itm.year; // See Range JSDoc in term.type.ts (|year| ≤ 10 is relative)
            }
            else {
                clone.year = targetYY;
            }
            window.push(clone);
        });
    }
    return window;
}
/**
 * ## registerTerm
 * Registration hook for Term plugins.
 */
export function registerTerm(term, state) {
    const rt = getRuntime();
    // Validate and persist in the state's discovery database.
    if (state)
        rt.addTerm(state, term);
    else if (rt.state)
        rt.addTerm(rt.state, term);
    rt.emit(sym.$Register, term);
}
/**
 * Resolves an anchored ordinal expression (e.g. "3rd day of #qtr.2", "last day of #szn.summer")
 * relative to a range-based Term plugin.
 *
 * @param termObj - The term plugin instance providing ranges via define()
 * @param groups - Capture groups from the ordinal expression (e.g. ord, term, value)
 * @param anchor - The reference Tempo instance
 * @returns The resolved Tempo instance, or undefined when not resolvable
 */
export function resolveRangeOrdinal(termObj, groups, anchor) {
    const rawDayOffset = groups.value ?? (isString(groups.ord) ? parseInt(groups.ord, 10) : undefined);
    if (!Number.isFinite(rawDayOffset) || rawDayOffset === 0)
        return undefined;
    const parts = (groups.term || '').slice(1).split('.');
    const subKey = parts[1];
    let range;
    if (isDefined(subKey) && subKey !== '') {
        const cycleNum = parseInt(subKey, 10);
        if (Number.isInteger(cycleNum) && cycleNum > 0) {
            range = isFunction(termObj.define) ? termObj.define.call(anchor, cycleNum, anchor) : undefined;
        }
        else {
            range = isFunction(termObj.define) ? termObj.define.call(anchor, subKey, anchor) : undefined;
        }
    }
    else {
        range = isFunction(termObj.define) ? termObj.define.call(anchor, false, anchor) : undefined;
    }
    if (!range || !range.start || !range.end)
        return undefined;
    const target = (rawDayOffset > 0)
        ? range.start.add({ days: rawDayOffset - 1 })
        : range.end.subtract({ days: Math.abs(rawDayOffset) });
    const targetNs = target.toDateTime().epochNanoseconds;
    const startNs = range.start.toDateTime().epochNanoseconds;
    const endNs = range.end.toDateTime().epochNanoseconds;
    return (targetNs < startNs || targetNs >= endNs)
        ? undefined
        : target;
}
/**
 * Resolves the difference between two Tempo instances in terms of a range-based Term plugin.
 * (e.g. t1.until(t2, '#qtr'), t2.since(t1, '#szn'))
 *
 * @param termObj - The term plugin instance providing ranges via define()
 * @param other - The target Tempo instance
 * @param _unit - The unit string (e.g. "#qtr")
 * @param tempo - The origin Tempo instance
 * @returns The signed count of ranges between tempo and other, or undefined
 */
export function resolveRangeDiff(termObj, other, _unit, tempo) {
    if (!isFunction(termObj?.define))
        return undefined;
    const rangeA = termObj.define.call(tempo, false, tempo);
    const rangeB = termObj.define.call(other, false, other);
    if (!rangeA?.start || !rangeA?.end || !rangeB?.start || !rangeB?.end)
        return undefined;
    const startANs = rangeA.start.toDateTime().epochNanoseconds;
    const startBNs = rangeB.start.toDateTime().epochNanoseconds;
    if (startANs === startBNs)
        return 0;
    // Invert for past target
    if (startBNs < startANs) {
        const positiveDiff = resolveRangeDiff(termObj, tempo, _unit, other);
        return isDefined(positiveDiff) ? -positiveDiff : undefined;
    }
    let count = 0;
    let currentRange = rangeA;
    const maxIterations = 10000;
    while (count < maxIterations) {
        const currentEndNs = currentRange.end.toDateTime().epochNanoseconds;
        if (currentEndNs > startBNs)
            break;
        count++;
        const nextRange = termObj.define.call(currentRange.end, false, currentRange.end);
        if (!nextRange?.start || !nextRange?.end || nextRange.start.toDateTime().epochNanoseconds <= currentRange.start.toDateTime().epochNanoseconds)
            return undefined;
        currentRange = nextRange;
    }
    return (count >= maxIterations) ? undefined : count;
}
/**
 * Resolves formatted term tokens (e.g. "{#qtr}", "{#qtr.label}", "{#szn.symbol}")
 * by delegating to the range definition of the term.
 *
 * @param termObj - The term plugin instance providing ranges via define()
 * @param token - The format token (e.g. "#qtr", "#qtr.label")
 * @param tempo - The reference Tempo instance being formatted
 * @returns The resolved string value, or undefined when not resolvable
 */
export function resolveRangeFormat(termObj, token, tempo) {
    if (!isFunction(termObj?.define))
        return undefined;
    const range = termObj.define.call(tempo, false, tempo);
    if (!range)
        return undefined;
    const parts = token.slice(1).split('.');
    const subKeys = parts.slice(1);
    // Top-level token without sub-path (e.g. "{#qtr}", "{#quarter}", "{#szn}")
    if (subKeys.length === 0) {
        const tokenName = parts[0]?.toLowerCase();
        const isScope = tokenName === termObj.scope?.toLowerCase();
        const val = isObject(range)
            ? (isScope ? (range.label ?? range.key) : (range.key ?? range.label))
            : range;
        return isDefined(val) ? String(val) : undefined;
    }
    // Traverse sub-keys (e.g. "{#qtr.label}", "{#szn.symbol}")
    let curr = range;
    for (const key of subKeys) {
        if (isNullish(curr) || !isSafeKey(key))
            return undefined;
        curr = curr[key];
    }
    if (isNullish(curr) || isFunction(curr))
        return undefined;
    if (isTempo(curr))
        return cast(curr).format('{yyyy}-{mm}-{dd}');
    return isObject(curr)
        ? String(curr.label ?? curr.key ?? curr.name ?? curr.value ?? curr)
        : String(curr);
}
